import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Loader2, Check, Users, Building2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { lawyerCasesAPI, kontakteAPI, type KontaktTag, type KontaktSearchResult } from '@/utils/api'
import { KontaktAutocomplete } from './KontaktAutocomplete'
import { CreateKontaktModal } from './kontakt-detail/CreateKontaktModal'

interface CreateCaseModalProps {
  onCreated?: (caseData: any) => void
  triggerButton?: React.ReactNode
}

export function CreateCaseModal({ onCreated, triggerButton }: CreateCaseModalProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Form state
  const [mandant, setMandant] = useState<KontaktSearchResult | null>(null)
  const [beklagter, setBeklagter] = useState<KontaktSearchResult | null>(null)
  const [customTitle, setCustomTitle] = useState('')

  // Tags for inline contact creation
  const [tags, setTags] = useState<KontaktTag[]>([])

  // Inline create modals
  const [showCreateMandant, setShowCreateMandant] = useState(false)
  const [showCreateBeklagter, setShowCreateBeklagter] = useState(false)

  // Load tags on mount
  useEffect(() => {
    kontakteAPI.getTags().then(setTags).catch(console.error)
  }, [])

  const resetForm = () => {
    setMandant(null)
    setBeklagter(null)
    setCustomTitle('')
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      resetForm()
    }
  }

  const handleSave = async () => {
    if (!mandant) {
      toast.error('Bitte Mandant auswählen')
      return
    }
    if (!beklagter) {
      toast.error('Bitte Beklagter auswählen')
      return
    }

    setIsSaving(true)
    try {
      const result = await lawyerCasesAPI.createCase({
        mandant_id: mandant.id,
        beklagter_id: beklagter.id,
        custom_title: customTitle || undefined,
      })

      toast.success('Fall erfolgreich erstellt')
      handleOpenChange(false)
      onCreated?.(result.case)

      // Navigate to the new case
      navigate({ to: '/lawyer/cases/$caseId', params: { caseId: String(result.case.id) } })
    } catch (err) {
      console.error('Failed to create case:', err)
      toast.error('Fall konnte nicht erstellt werden')
    } finally {
      setIsSaving(false)
    }
  }

  // Check if form is valid
  const isValid = mandant !== null && beklagter !== null

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          {triggerButton || (
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Neuer Fall
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Neuen Fall erstellen</DialogTitle>
            <DialogDescription>
              Wählen Sie den Mandanten und den Beklagten aus, um einen neuen Fall zu erstellen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Mandant (Client) */}
            <div className="space-y-2">
              <KontaktAutocomplete
                value={mandant}
                onChange={setMandant}
                placeholder="Mandant suchen..."
                label="Mandant *"
                onCreateNew={() => setShowCreateMandant(true)}
              />
              {mandant && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {mandant.kontakt_type === 'person' ? (
                    <Users className="h-3 w-3" />
                  ) : (
                    <Building2 className="h-3 w-3" />
                  )}
                  <span>{mandant.kontakt_type === 'person' ? 'Person' : 'Unternehmen'}</span>
                </div>
              )}
            </div>

            {/* Beklagter (Defendant) */}
            <div className="space-y-2">
              <KontaktAutocomplete
                value={beklagter}
                onChange={setBeklagter}
                placeholder="Beklagter suchen (z.B. Fluggesellschaft)..."
                label="Beklagter *"
                tagFilter="airline" // Suggest airlines first
                onCreateNew={() => setShowCreateBeklagter(true)}
              />
              {beklagter && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  {beklagter.kontakt_type === 'person' ? (
                    <Users className="h-3 w-3" />
                  ) : (
                    <Building2 className="h-3 w-3" />
                  )}
                  <span>{beklagter.kontakt_type === 'person' ? 'Person' : 'Unternehmen'}</span>
                  {beklagter.iata_code && (
                    <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">
                      {beklagter.iata_code}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Custom Title (optional) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">
                Eigener Titel <span className="text-slate-400">(optional)</span>
              </Label>
              <Input
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder={
                  mandant && beklagter
                    ? `${mandant.kontakt_type === 'person' ? (mandant.display_name.split(' ').pop()?.toUpperCase() || mandant.display_name) : mandant.display_name} // ${beklagter.display_name}`
                    : 'z.B. Müller // Lufthansa'
                }
              />
              <p className="text-xs text-slate-500">
                Leer lassen für automatischen Titel basierend auf Mandant und Beklagter.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
              >
                Abbrechen
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !isValid}>
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Fall erstellen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline Create Mandant Modal */}
      <CreateKontaktModal
        tags={tags}
        mode="inline"
        externalOpen={showCreateMandant}
        onOpenChange={setShowCreateMandant}
        onCreatedInline={(kontakt) => {
          setMandant(kontakt)
          setShowCreateMandant(false)
        }}
      />

      {/* Inline Create Beklagter Modal */}
      <CreateKontaktModal
        tags={tags}
        mode="inline"
        externalOpen={showCreateBeklagter}
        onOpenChange={setShowCreateBeklagter}
        onCreatedInline={(kontakt) => {
          setBeklagter(kontakt)
          setShowCreateBeklagter(false)
        }}
      />
    </>
  )
}
